import {
  Collapsible,
  Flex,
  FlexColumn,
  FlexGrow,
  IconButton,
  InputGroup,
  InputGroupBreak,
  InputRow,
  ModelCheckbox,
  ModelDateDMY,
  ModelInput,
  RadioListInput,
  RadioYesNoInput,
  useFormConfigurationContext,
  useModelFormUpdate,
} from '@equifinance/shared';
import styled from 'styled-components';
import { useEffect } from 'react';
import { OS1OS3PriorityRegisteredType, OS1OS3Title, useChecklistContext } from '../../../../../../contexts';
import { DeleteIcon } from '../../../../../Account/components/Icons';

const TitleWrapper = styled(FlexColumn)`${({ theme }) => `
  padding: ${theme.padding[1]} ${theme.padding[5]};
  gap: ${theme.padding[5]};
`}`;

const CheckboxContainer = styled.div`
  width: fit-content;
`;

const TitleHeader = styled(Flex)`
  justify-content: space-between;
  width: 100%;
`;

const DeleteIconButton = styled(DeleteIcon)`${({ theme }) => `
  color: ${theme.colours.primary[1]};
  font-size: ${theme.fontSize[3]};
`}`;

export default function TitleSection({ title, index, numOfTitles }: { title: OS1OS3Title, index: number, numOfTitles: number }) {
  const {
    expandedStates, handleToggle, checklist, setExpandedState,
  } = useChecklistContext();
  const path = `os1os3.titles[${index}]`;

  const { id, daylist, manualTaskAdded } = title;

  useEffect(() => {
    if (!id) return;
    setExpandedState(id, true);
  }, [id]);

  const update = useModelFormUpdate();
  const { isDisabled } = useFormConfigurationContext();

  const onDeleteTitle = (e: any) => {
    e.stopPropagation();
    if (isDisabled) return;
    const updatedTitles = checklist?.os1os3?.titles?.filter((t) => t.id !== id);
    update({ 'os1os3.titles': updatedTitles ?? [] });
  };

  return (
    <Collapsible
      collapsed={!expandedStates[id]}
      onClickCollapse={() => handleToggle(id)}
      title={(
        <TitleHeader>
          <div>
            {`Title ${index + 1}`}
          </div>
          {numOfTitles > 1 && <IconButton onClick={onDeleteTitle}><DeleteIconButton /></IconButton>}
        </TitleHeader>
      )}
      level={1}
    >
      <TitleWrapper>
        <InputGroup>
          <InputRow>
            <ModelInput
              label="Title number"
              field={`${path}.titleNumber`}
              mutateValue={(v) => v?.toUpperCase()}
              required
            />
            <RadioYesNoInput
              label="Is this an equitable charge?"
              field={`${path}.equitableCharge`}
              required
            />
          </InputRow>
        </InputGroup>
        <InputGroupBreak />
        <InputGroup>
          <InputRow>
            <RadioYesNoInput
              label="Is there anything else registered on the daylist?"
              field={`${path}.daylist`}
              required
            />
            <FlexGrow>
              <CheckboxContainer>
                <ModelCheckbox
                  label="Manual task added"
                  field={`${path}.manualTaskAdded`}
                  isCompleteCheckbox
                  successColor={manualTaskAdded}
                  touchOverride={daylist !== null}
                  disabled={daylist === false}
                />
              </CheckboxContainer>
            </FlexGrow>
          </InputRow>
        </InputGroup>
        <InputGroupBreak />
        <InputGroup>
          <InputRow>
            <RadioListInput
              label="Which priority has been registered?"
              field={`${path}.priorityRegistered`}
              options={OS1OS3PriorityRegisteredType}
              required
            />
          </InputRow>
        </InputGroup>
        <InputGroupBreak />
        <InputGroup>
          <InputRow>
            <ModelDateDMY
              label="When was it last registered?"
              field={`${path}.lastRegistered`}
              showDay
              required
            />
            <ModelDateDMY
              label="Expiry date"
              field={`${path}.expiryDate`}
              showDay
              required
            />
          </InputRow>
        </InputGroup>
      </TitleWrapper>
    </Collapsible>
  );
}
